import { unwrap } from "@brad-tipper/app-frontend";

import { api } from "~/api/client";
import type { Json } from "~/api/tables";
import { useAsyncAction } from "~/hooks/useAsyncAction";
import { useAsyncResource } from "~/hooks/useAsyncResource";

type AccountExport = {
  exportedAt: string;
  dataController: string;
  user: Json;
  workspaces: Json[];
  workspaceMembers: Json[];
  workspaceInvitations: Json[];
  clients: Json[];
  projectStatuses: Json[];
  projects: Json[];
  tasks: Json[];
  userRates: Json[];
  timeEntries: Json[];
  invoices: Json[];
  recurringInvoices: Json[];
  invoiceReminders: Json[];
  invoiceAmendments: Json[];
  invoiceRefunds: Json[];
  taxSetAsideEntries: Json[];
  reportViews: Json[];
  notifications: Json[];
  notificationReads: Json[];
  contactSubmissions: Json[];
  feedbackSubmissions: Json[];
  revenuecatWebhookEvents: Json[];
};

export function useAccountPrivacy() {
  const status = useAsyncResource(
    async () => unwrap(api.getAccountDeletion()),
    [],
    null,
  );

  const exportAccount = useAsyncAction(async () => {
    const data = await unwrap(api.exportAccount());
    return data as unknown as AccountExport;
  });

  const requestDeletion = useAsyncAction(async (confirmedEmail: string) => {
    await unwrap(
      api.requestAccountDeletion({
        body: { confirmation: "DELETE", email: confirmedEmail.trim() },
      }),
    );
    await status.refresh();
  });

  const cancelDeletion = useAsyncAction(async () => {
    await unwrap(api.cancelAccountDeletion());
    await status.refresh();
  });

  return {
    deletionRequestedAt: status.data?.requestedAt ?? null,
    scheduledForDeletionAt: status.data?.scheduledFor ?? null,
    isScheduledForDeletion: Boolean(status.data?.scheduledFor),
    loading: status.loading,
    error:
      status.error ??
      exportAccount.error ??
      requestDeletion.error ??
      cancelDeletion.error,
    refresh: status.refresh,
    exportAccount: exportAccount.run,
    exportPending: exportAccount.pending,
    requestDeletion: requestDeletion.run,
    requestDeletionPending: requestDeletion.pending,
    cancelDeletion: cancelDeletion.run,
    cancelDeletionPending: cancelDeletion.pending,
  };
}
